import { createFileRoute, Link } from '@tanstack/react-router';
import { useState } from 'react';
import { ledgerToCsv, type LedgerRow } from '@pirouette/shared';
import { supabase } from '@/lib/supabase';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { toast } from '@/lib/toast';

export const Route = createFileRoute('/admin/billing/export')({
  component: BillingExport,
});

function BillingExport() {
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [exporting, setExporting] = useState(false);

  async function handleExport() {
    setExporting(true);
    try {
      // Fetch charges joined with allocations + family + dancer names.
      // RLS filters to admin's full visibility automatically.
      let q = supabase
        .from('charge_with_allocations')
        .select(
          'id, created_at, family_id, dancer_id, category, description, amount_cents, allocated_cents, due_date, status, stripe_invoice_id, stripe_payment_intent_id, billed_at, enrollment_id, competition_routine_id, updated_at, notes',
        )
        .order('created_at', { ascending: true });
      if (from) q = q.gte('created_at', `${from}T00:00:00Z`);
      if (to) q = q.lte('created_at', `${to}T23:59:59Z`);
      const { data: charges, error } = await q;
      if (error) throw error;

      const familyIds = [
        ...new Set((charges ?? []).map((c) => (c as { family_id: string }).family_id)),
      ];
      const dancerIds = [
        ...new Set(
          (charges ?? [])
            .map((c) => (c as { dancer_id: string | null }).dancer_id)
            .filter((id): id is string => id !== null),
        ),
      ];

      const [familiesRes, dancersRes] = await Promise.all([
        familyIds.length > 0
          ? supabase.from('families').select('id, primary_guardian_name').in('id', familyIds)
          : { data: [], error: null },
        dancerIds.length > 0
          ? supabase
              .from('dancers')
              .select('id, first_name, last_name, preferred_name')
              .in('id', dancerIds)
          : { data: [], error: null },
      ]);
      if (familiesRes.error) throw familiesRes.error;
      if (dancersRes.error) throw dancersRes.error;

      const familyName = new Map(
        ((familiesRes.data ?? []) as { id: string; primary_guardian_name: string }[]).map((f) => [
          f.id,
          f.primary_guardian_name,
        ]),
      );
      const dancerName = new Map(
        (
          (dancersRes.data ?? []) as {
            id: string;
            first_name: string;
            last_name: string;
            preferred_name: string | null;
          }[]
        ).map((d) => [d.id, `${d.preferred_name ?? d.first_name} ${d.last_name}`]),
      );

      const rows: LedgerRow[] = (charges ?? []).map((c) => {
        const row = c as Record<string, unknown>;
        return {
          charge: row as never,
          family_name: familyName.get(row.family_id as string) ?? '',
          dancer_name: dancerName.get((row.dancer_id as string | null) ?? '') ?? null,
          allocated_cents: (row.allocated_cents as number) ?? 0,
        };
      });

      const csv = ledgerToCsv(rows);
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `pirouette-ledger-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      toast({
        title: 'Export ready',
        description: `${rows.length} charges exported`,
        variant: 'success',
      });
    } catch (err) {
      toast({
        title: 'Export failed',
        description: err instanceof Error ? err.message : 'Unknown error',
        variant: 'error',
      });
    } finally {
      setExporting(false);
    }
  }

  return (
    <div className="max-w-xl space-y-8">
      <header>
        <Link
          to="/admin/billing"
          className="text-[var(--color-ink-muted)] text-[var(--text-sm)] hover:text-[var(--color-ink)]"
        >
          ← Billing
        </Link>
        <h1 className="mt-2">Export ledger</h1>
        <p className="mt-2 max-w-prose text-[var(--color-ink-muted)]">
          Downloads a CSV of charges with allocated payments and remaining balances. Pass dates to
          filter by created_at.
        </p>
      </header>

      <section className="surface-elevated grid gap-4 p-5">
        <div>
          <Label htmlFor="exp-from">From</Label>
          <Input id="exp-from" type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
        </div>
        <div>
          <Label htmlFor="exp-to">To</Label>
          <Input id="exp-to" type="date" value={to} onChange={(e) => setTo(e.target.value)} />
        </div>
        <Button onClick={() => void handleExport()} disabled={exporting}>
          {exporting ? 'Exporting…' : 'Download CSV'}
        </Button>
      </section>
    </div>
  );
}
